import {
  APPLICATION_STATE_LABELS,
  isSubscriptionClosed,
} from '@internal/components/projects/helper/application-state';
import { ApplicationState } from '@internal/types/projects';

import {
  APPLICATION_ACCEPTED_TEXT,
  APPLICATION_ACCEPTED_TITLE,
  APPLICATION_PAUSED_TITLE,
  CLIENT_REVIEW_PREPARATION_TEXT,
  CLIENT_REVIEW_TEXT,
  IN_PROGRESS_LABEL,
  IN_REVIEW_TEXT,
  IN_REVIEW_TITLE,
  NOT_SELECTED_TEXT,
  OFFER_CANCELLED_TEXT,
  PROJECT_CLOSED_TEXT,
  PROJECT_ENDED_TITLE,
  ROLE_ON_HOLD_TEXT,
  SELECTED_BY_CLIENT_TEXT,
  SUBSCRIPTION_CLOSED_TEXT,
  WITHDRAWN_TEXT,
} from './application-copy';
import {
  ApplicationTarget,
  BannerModel,
  BuildInput,
  exploreProjects,
} from './application-model.types';

const ROLE_TARGETS: ReadonlySet<ApplicationTarget> = new Set([
  ApplicationTarget.Roles,
  ApplicationTarget.RolesExplore,
]);

const banner = (
  title: string,
  detail: string,
  next: BannerModel['next'] = null,
  allowFallback = true
): BannerModel => ({ title, detail, next, allowFallback });

export const buildClosedProjectBanner = (): BannerModel =>
  banner(
    APPLICATION_STATE_LABELS[ApplicationState.ProjectClosed],
    OFFER_CANCELLED_TEXT,
    null,
    false
  );

export const buildOfferBanner = (
  state: ApplicationState | null
): BannerModel | null =>
  state === ApplicationState.OfferDeclined
    ? banner(
        APPLICATION_STATE_LABELS[state],
        WITHDRAWN_TEXT,
        exploreProjects('Explore other projects'),
        false
      )
    : null;

const buildStateBanner = (input: BuildInput): BannerModel | null => {
  const { state, project } = input;
  const title = APPLICATION_STATE_LABELS[state];
  const exploreOthers = exploreProjects('Explore other projects');

  if (isSubscriptionClosed(state, project)) {
    return banner(PROJECT_ENDED_TITLE, SUBSCRIPTION_CLOSED_TEXT, exploreOthers);
  }
  switch (state) {
    case ApplicationState.NotSelected:
      return banner(title, NOT_SELECTED_TEXT, exploreOthers);
    case ApplicationState.Withdrawn:
      return banner(title, WITHDRAWN_TEXT, exploreOthers);
    case ApplicationState.ProjectClosed:
    case ApplicationState.OfferCancelled:
      return banner(
        APPLICATION_STATE_LABELS[ApplicationState.ProjectClosed],
        project.hasCandidateConfirmedApplication
          ? PROJECT_CLOSED_TEXT
          : OFFER_CANCELLED_TEXT,
        exploreProjects('Explore projects')
      );
    case ApplicationState.Accepted:
      return banner(title, SELECTED_BY_CLIENT_TEXT, null, false);
    case ApplicationState.WithMatching:
      return banner(title, CLIENT_REVIEW_PREPARATION_TEXT);
    case ApplicationState.UnderClientReview:
      return banner(title, CLIENT_REVIEW_TEXT);
    default:
      return null;
  }
};

const buildReviewBanner = ({
  project,
  profileReady,
  roleProgress: rp,
}: BuildInput): BannerModel => {
  if (rp.stopped) {
    return banner(APPLICATION_PAUSED_TITLE, ROLE_ON_HOLD_TEXT, null, false);
  }
  if (!profileReady) {
    return banner(
      IN_PROGRESS_LABEL,
      'Complete your profile to move your application forward.',
      { label: 'Complete profile', target: ApplicationTarget.Profile }
    );
  }
  if (!rp.verified) {
    return banner(
      IN_PROGRESS_LABEL,
      'Finish your role checks. Your progress is shared across applications.',
      {
        label: 'Continue verification',
        target: rp.started
          ? ApplicationTarget.Roles
          : ApplicationTarget.RolesExplore,
      }
    );
  }
  if (project.isApplicationAccepted) {
    return banner(APPLICATION_ACCEPTED_TITLE, APPLICATION_ACCEPTED_TEXT);
  }
  return banner(IN_REVIEW_TITLE, IN_REVIEW_TEXT);
};

export const buildBanner = (input: BuildInput): BannerModel => {
  const content = buildStateBanner(input) ?? buildReviewBanner(input);
  const roleLink = content.next && ROLE_TARGETS.has(content.next.target);
  return input.paused && roleLink ? { ...content, next: null } : content;
};

export const getApplicationListLabel = (
  state: ApplicationState,
  input: Pick<BuildInput, 'project' | 'profileReady' | 'roleProgress'>
): string => {
  if (isSubscriptionClosed(state, input.project)) return PROJECT_ENDED_TITLE;
  if (state !== ApplicationState.InReview) {
    return APPLICATION_STATE_LABELS[state];
  }
  if (input.roleProgress.stopped) return APPLICATION_PAUSED_TITLE;
  if (!input.profileReady || !input.roleProgress.verified) {
    return IN_PROGRESS_LABEL;
  }
  if (input.project.isApplicationAccepted) return APPLICATION_ACCEPTED_TITLE;
  return IN_REVIEW_TITLE;
};
